<template>
  <v-card
    :class="'groceryCard' + (small ? ' small ' : '') + (item.gathered ? ' gathered ' : '')"
    :loading="loading"
    :elevation="0"
  >
    <template #loader="{ isActive }">
      <div v-if="isActive || loading" class="cardGlassLoader d-flex align-center justify-center">
        <v-progress-circular
          indeterminate
          color="primary"
          size="32"
          width="3"
        />
      </div>
    </template>

    <v-card-text class="pa-0">
      <v-row no-gutters class="flex-nowrap">
        <v-col v-if="!(item.gathered && small)" cols="auto" align-self="center" class="ma-2">
          <v-img
            v-if="item.ingredient && item.ingredient.image_url"
            :src="item.ingredient.image_url"
            :height="small ? 50 : 100"
            :width="small ? 50 : 100"
            :class="item.gathered ? 'dimmed' : ''"
          />
          <div
            v-if="!item.ingredient || !item.ingredient.image_url"
            class="noIngredient"
          />
        </v-col>

        <v-col v-if="!(item.gathered && small)" class="middleContainer my-2">
          <v-row no-gutters>
            <v-col>
              <p
                :class="'xsName upperFirstLetter mb-2 '+(item.gathered ? 'dimmed' : '')"
              >
                {{ item.name }}
              </p>
            </v-col>
          </v-row>
          <v-row no-gutters align="center" class="flex-nowrap">
            <v-col v-if="linkedGroceryMeals.length" cols="auto">
              <linked-meal-tooltip :linked-grocery-meals="linkedGroceryMeals" :ingredient-id="item.ingredient.id" color="primary" />
            </v-col>
            <v-col v-if="small && item.ingredient && (item.ingredient.need_bag || item.ingredient.need_box)" cols="auto" class="ml-1">
              <v-icon class="bagBoxMarker" size="small" color="primary" :title="item.ingredient.need_bag ? 'Besoin d\'un sac' : 'Besoin d\'une boîte'">
                {{ item.ingredient.need_bag ? 'mdi-shopping' : item.ingredient.need_box ? 'mdi-basket-fill' : '' }}
              </v-icon>
            </v-col>

            <v-spacer />

            <v-col cols="auto" align-self="center">
              <v-btn
                icon
                size="x-small"
                variant="text"
                color="grey-darken-1"
                @click="editGrocery"
              >
                <v-icon :size="24">
                  mdi-pencil
                </v-icon>
              </v-btn>
              <v-btn
                icon
                variant="text"
                size="x-small"
                color="grey-darken-1"
                @click="deleteGrocery"
              >
                <v-icon :size="24">
                  mdi-delete
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-col>

        <v-col v-if="!(item.gathered && small)" cols="auto">
          <span class="separator mr-0">&nbsp;</span>
        </v-col>

        <v-col v-if="!(item.gathered && small)" cols="auto" align-self="center">
          <div class="quantity">
            <v-text-field
              v-model="amount"
              name="amount"
              variant="outlined"
              hide-details
              density="compact"
              :suffix="item.unit === 'nb' ? '' : item.unit"
              :disabled="isOffline || item.gathered"
              @update:model-value="updateAmount"
            />
            <div v-if="approximateItemText" class="approxText text-caption text-grey-darken-1 text-center mt-1">
              {{ approximateItemText }}
            </div>
          </div>
        </v-col>

        <v-col v-if="item.gathered && small">
          <v-row dense align="center" class="mx-0">
            <v-col cols="6">
              {{ grocerySummary(item.id) }}
            </v-col>
            <v-spacer />
            <v-col v-if="linkedGroceryMeals.length" cols="auto">
              <linked-meal-tooltip :linked-grocery-meals="linkedGroceryMeals" :ingredient-id="item.ingredient.id" />
            </v-col>
            <v-col cols="auto">
              <v-btn
                icon
                variant="text"
                size="small"
                color="grey-darken-1"
                @click="deleteGrocery"
              >
                <v-icon size="x-large">
                  mdi-delete
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
        <v-col
          cols="auto"
          align-self="stretch"
          :class="item.gathered && !small ? 'checkContainer outlined' : 'checkContainer'"
        >
          <v-btn
            variant="text"
            :color="item.gathered && !small ? 'rgb(var(--v-theme-background-darken-2))' : 'white'"
            class="gatherBtn"
            :ripple="false"
            :disabled="isOffline"
            @click="gather(!item.gathered)"
          >
            <v-icon size="x-large">
              {{ item.gathered ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import _ from "lodash"
import { useRepo } from "pinia-orm"
import { Globalconfig } from "@/models/Globalconfig"
import { Grocery, GroceryRepo } from "@/models/Grocery"
import { GroceryMeal } from "@/models/GroceryMeal"
import { Ingredient } from "@/models/Ingredient"
import LinkedMealTooltip from '@/components/LinkedMealTooltip'
import emitter from 'tiny-emitter/instance'
import moment from 'moment'

export default {
  name: "GroceryCard",
  components: {
    LinkedMealTooltip
  },
  props: {
    item: {
      type: Object,
      default: () => {}
    }
  },
  emits: ['edit'],
  data: () => ({
    loading: false,
    linkedGroceryMeals: [],
    amount: 0
  }),
  computed: {
    isOffline() {
      return useRepo(Globalconfig).find('isOffline').value
    },
    ingredientUnits() {
      return Ingredient.units()
    },
    small() {
      return this.$vuetify.display.xs
    },
    approximateItemText() {
      if (!this.item || !this.item.ingredient) {
        return ''
      }
      const unitMass = Number(this.item.ingredient.unit_mass) || 0
      if (unitMass <= 0) {
        return ''
      }

      const unit = this.item.unit
      const amount = Number(this.amount) || 0
      if (amount <= 0) {
        return ''
      }

      let amountInGrams = null
      if (unit === 'g') {
        amountInGrams = amount
      } else if (unit === 'kg') {
        amountInGrams = amount * 1000
      } else if (unit === 'ml') {
        amountInGrams = amount
      } else if (unit === 'cl') {
        amountInGrams = amount * 10
      } else if (unit === 'l') {
        amountInGrams = amount * 1000
      }

      if (amountInGrams === null) {
        return ''
      }

      const nbItems = Math.round((amountInGrams / unitMass) * 10) / 10
      if (nbItems <= 0) {
        return ''
      }

      const formattedNb = Number.isInteger(nbItems) ? nbItems : nbItems.toFixed(1).replace('.', ',')
      let displayName = ''

      if (nbItems > 1) {
        displayName = this.item.ingredient.plural || this.item.ingredient.name || this.item.name || ''
      } else {
        displayName = this.item.ingredient.name || this.item.name || ''
      }

      return `env. ${formattedNb} ${displayName.trim().toLowerCase()}`
    }
  },
  watch: {
    item: {
      handler(newval, oldVal) {
        this.refreshItem()
      },
      deep: true
    }
  },
  mounted () {
    this.refreshItem()
  },
  methods: {
    refreshItem() {
      if (this.item.ingredient) {
        this.linkedGroceryMeals = useRepo(GroceryMeal)
          .where("grocery_id", this.item.id)
          .with("meal")
          .get()
        this.linkedGroceryMeals.sort((a, b) => {
            return moment(a.meal.date).isAfter(b.meal.date) ? 1 : -1
          })
      }
      this.amount = this.item.amount
    },
    updateAmount: _.debounce(async function(newValue) {
      if(!isNaN(newValue) && !isNaN(parseFloat(newValue))) {
        this.loading = true
        // DO NOT USE ARROW FUNCTION HERE OR "this" GETS LOST
        await useRepo(GroceryRepo).$update(this.item.id, {
          ...this.item,
          amount: newValue
        })
        this.loading = false
      }
      else {
        this.amount = this.item.amount
      }
    }, 1000),
    gather: async function(value) {
      useRepo(Grocery).save({
        id: this.item.id,
        gathered: value
      })
      const data = {
        ...useRepo(Grocery).find(this.item.id)
      }
      // Async update to make UI faster
      useRepo(GroceryRepo).$update(data.id, data)
    },
    deleteGrocery() {
      emitter.emit('showConfirm', {
        title: 'Suppression',
        text: 'Voulez-vous supprimer l\'élément "'+this.item.name+'" ?',
        validate: async () => {
          this.loading = true
          await useRepo(GroceryRepo).$delete(this.item.id)
          this.loading = false
        }
      })
    },
    editGrocery() {
      this.$emit('edit', this.item.id)
    },
    grocerySummary(id) {
      return useRepo(Grocery).find(id).summary()
    }
  }
}
</script>

<style lang="scss" scoped>
.groceryCard:not(:last-child) {
  margin-bottom: 5px;
}
.groceryCard {
  position: relative;
  z-index: 1;
  border-radius: 16px !important;
  transition: background-color 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;

  :deep(.v-card__loader) {
    inset: 0;
    height: 100%;
    z-index: 10;
    pointer-events: none;
  }

  .cardGlassLoader {
    width: 100%;
    height: 100%;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.55);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    pointer-events: auto;
  }

  &:not(.gathered) {
    background: rgba(255, 255, 255, 0.75) !important;
    backdrop-filter: blur(12px) saturate(160%);
    -webkit-backdrop-filter: blur(12px) saturate(160%);
    border: 1px solid rgba(var(--v-theme-primary), 0.18) !important;
    box-shadow: 0 8px 20px -4px rgba(0, 0, 0, 0.05), 0 2px 6px -2px rgba(0, 0, 0, 0.02) !important;

    &:hover {
      z-index: 5;
      background: rgba(255, 255, 255, 0.92) !important;
      box-shadow: 0 12px 26px -4px rgba(var(--v-theme-primary), 0.14), 0 4px 10px -2px rgba(0, 0, 0, 0.05) !important;
      border-color: rgba(var(--v-theme-primary), 0.55) !important;
    }
  }

  &.gathered {
    background: rgba(255, 255, 255, 0.45) !important;
    border: 1px solid rgba(0, 0, 0, 0.12) !important;
    box-shadow: none !important;
    color: rgba(0, 0, 0, .6);
  }

  .separator {
    border-left: 1px solid rgba(0, 0, 0, 0.08);
    width: 1px;
    height: 100%;
    min-height: 100%;
    align-self: stretch;
    margin: 0 4px;
    display: inline-block;
  }

  .middleContainer {
    display: flex;
    flex-direction: column;
  }

  .chipGroup {
    white-space: nowrap;
    overflow: auto;
    width: 0; // WHY DO WE NEED THIS TO AVOID OVERFLOW ??????
    padding-bottom: 3px;

    > * {
      flex: 0 0 auto; // Since parent is flex by adding media query classes
    }
  }
}
</style>
<style lang="scss">
.groceryCard {
  .v-card__text {
    padding: 8px;
  }

  .quantity {
    width: 75px;
    margin: 2px;

    * {
      padding: 0;
    }

    input {
      text-align: center;
      font-size: 1.5em;
      padding: 2px;
    }

    .v-text-field__suffix {
      padding-right: 4px;
    }

    .approxText {
      line-height: 1.1;
      font-size: 0.72rem;
    }
  }

  .unit {
    .v-field__input {
      text-align: center;
      width: 100%;
      justify-content: center;
      padding-left: 5px;
    }
  }

  .noIngredient {
    height: 100%;
    width: 100px;
    padding-top: 100%;
    position: relative;

    &:after {
      content: "";
      border: 2px dashed rgb(var(--v-theme-background-darken-1));
      border-radius: 50%;
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
    }
  }

  &.small .noIngredient {
    width: 50px;
  }

  .checkContainer {
    background-color: rgb(var(--v-theme-background-darken-2));
    border-top-right-radius: 15px;
    border-bottom-right-radius: 15px;

    display: flex;
    justify-content: center;
    align-items: center;

    &.outlined {
      background-color: transparent;
      border-left: 1px solid rgba(0, 0, 0, 0.06);
    }

    .gatherBtn {
      height: 100%;
      min-width: 32px;
      padding: 0;
    }
  }

  .xsName {
    color: rgb(var(--v-theme-primary));
    line-height: 1.5em;
    font-size: 1.5em;
    font-weight: 600;
    align-self: center;
    margin: 0;
    border-bottom: 1px solid rgba(var(--v-theme-primary), 0.18);
  }

  .mealList {
    .meal:last-of-type {
      margin-bottom: 0 !important;
    }
  }

  .bagBoxMarker {
    border: 1px solid rgb(var(--v-theme-primary));
    border-radius: 50%;
    padding: 10px;
  }

  .dimmed {
    opacity: .5;
  }
}
</style>